/**
 * SalesForecast.tsx — Sales / Reports → the Forecast TAB (Phase 2 / 2.3b,
 * F-6, §18 U-1..U-10). One read, GET /v1/sales/forecast for the selected
 * period, rendered as three co-equal parts in this order:
 *
 *   1. ATTRIBUTION (U-4) — a panel ABOVE the grid, equal in weight, stating
 *      both halves (won value that can attain a target and won value that
 *      cannot, each with its count) and the verdict. The verdict is the
 *      server's `attainmentMeasurable`, RENDERED not recomputed (R1): the
 *      threshold lives server-side so two surfaces cannot disagree. It sits
 *      above the grid because on every new tenant — and on Fourth Coffee
 *      today, 99% of won value unattributed — the grid is the footnote and
 *      this panel is the number.
 *   2. ATTAINMENT BY REP (U-5) — the rep grid. A null target reads "No
 *      target set" and a null attainment "—": words, never 0 / 0% / blank.
 *      A real £0 target reads £0.00. Rows exist only for employees with a
 *      quota or a deal in the period; an empty grid is a state, not an error.
 *   3. PIPELINE BY FORECAST CATEGORY (U-6, U-7) — the TENANT-WIDE roll-up,
 *      rendered from `rollup` ALONE and never derived from the rows: a
 *      tenant with no employees (Futured Group) has empty rows and a real
 *      pipeline here. All five buckets always render, zeros included, colour
 *      bound by slug.
 *
 * U-9: excludedNonGbp > 0 renders one quiet line (the nonGbpNote idiom).
 * WON is period-bound; OPEN pipeline is as-of-now (the file header of
 * forecast.go says so, and the roll-up's subtitle repeats it here).
 *
 * 2.3c (P2-11, §19 O-7) — THE OVERRIDE, RENDERED: the rep grid gains an
 * "Overrides" column (count · Σ gap, pairs in the title); the roll-up is the
 * EFFECTIVE view (override category/amount where a row exists) and carries
 * the coverage line — "N of M open deals carry an override (£X of £Y)" —
 * and, when the server says every open deal is overridden (allOverridden,
 * RENDERED not recomputed: the Fourth Coffee shape, 1 of 1), the statement
 * that the effective view IS the override view with the rep view drawn
 * beside it. Every overridden deal is listed with its gap chip ("Rep: Best
 * case → Override: Commit, −£4,000.00"). The attribution panel is UNCHANGED:
 * an override is a forecast of what is open, not a fact about what closed.
 *
 * §33 (W-1..W-7) — THE WON-CELL DRILL. A rep's won cell drills to the deals
 * behind it through the SHARED ReportsDrillView (one component, one
 * reconciliation path — SB-12; relocated to its own file under W-5, never
 * forked), as a NEW kind 'forecastWon' whose every field is copied from
 * this tab's /forecast RESPONSE — employee, wonPence / wonCount /
 * wonExcludedNonGbp and the period — never from the picker's state. The
 * fetch is the server's own predicate for the cell (stage closed_won, the
 * linked employee, closed_at inside the period; the 2.3b F-7 range on GET
 * /v1/sales/deals), and the gate is count-then-pence. NOT a reuse of 'rep'
 * (see reportsDrill.ts): that kind is /reports' performer row and would
 * cross populations silently. The cell is a button ONLY while its figure
 * exists (wonCount + wonExcludedNonGbp > 0, W-4): £0.00 · 0 deals stays
 * text, so an empty row never drills to an empty state. Drill state lives
 * here and the early return sits under the strip, so title and tabs stay
 * (the ReportsBody shape); "Back to forecast" restores the same grid from
 * the same response, no refetch.
 *
 * F2 (Forecast programme, register row 70) — THE HERO above all three:
 * ForecastHero.tsx over forecastHero.ts, spec §3.2 under ADOPTION §18 and the
 * 2026-09-29 rulings (insight rule 1 withheld; targets read attributed won).
 *
 * F3 (register row 71) — SUGGESTED CATEGORIES under the hero:
 * ForecastSuggestions.tsx over forecastSuggestions.ts, spec §3.3 under
 * ADOPTION §18.6 (single Accept only; Accept all REFUSED). After each accept
 * or undo the tab REFETCHES the same period SILENTLY — the last response
 * stays on screen until the new one lands, so the card keeps its "✓ Set"
 * rows and the hero and roll-ups recalculate from the wire, never locally.
 *
 * F4a (register row 72) — spec §3.4–§3.6 in the spec's grid: Categorised
 * share + Attribution (1/3) beside the suggestions, then the NEXT period's
 * look-ahead beside By forecast category, then the rep grid. ForecastCards.tsx
 * over forecastCards.ts. Attribution stays co-equal and always present (R1).
 *
 * F5 (register row 73) — spec §3.7, By rep: the rep grid becomes
 * ForecastByRep.tsx over forecastByRep.ts — the target column with pace
 * colours, "Set target" inline and "Set targets" (the quota PUT, §18.7), the
 * share-of-weighted bar, and the Unattributed row so the won column adds up.
 * The U-9 non-GBP note moved with the grid. U-5 columns RULED 2026-10-01:
 * the Attainment column is gone (the Target cell carries it, "No target set"
 * untouched); Overrides renders only while some row has an override.
 *
 * F6 (register row 74) — spec §3.8, the tab's STATES. LOADING is
 * ForecastSkeleton: the page's own card shapes, no text and no figure (never
 * a "£0" the server has not sent). EMPTY is page-level and has ONE
 * condition, "no deals at all" — which /forecast cannot state (its won
 * figures are period-bound), so it is read from the SHELL's shared
 * /v1/sales/stats (salesFigures.ts): a dependency of this page on the shell,
 * taken on purpose and fenced in forecastView.tenantHasNoDeals — the stats
 * read must have LANDED, count nothing, and the forecast response must agree.
 * A failed or absent stats read renders the normal view, never "no deals".
 * The period's own states (ended → retrospective, future → Stretch) live in
 * the hero. NOTHING below the hero changes with the period's state (RULED
 * 2026-10-02): the suggestions, Categorised share, the look-ahead and the
 * target buttons stay as-of-today on an ended period — register row 96.
 *
 * Still not in this slice (W-8, deferred): the per-rep category breakdown
 * the wire also carries (openByCategory) — a render decision first, and it
 * reads zero on every linked rep of the live tenant today.
 */
import { useEffect, useState } from 'react'
import { EmptyState, ErrorState } from '../../DashboardShared'
import { useAsync, fetchSalesForecast } from '../../api'
import { useSalesFigures } from '../../salesFigures'
import { ForecastSkeleton } from './ForecastSkeleton'
import { tenantHasNoDeals } from './forecastView'
import { ReportsDrillView } from './ReportsDrillView'
import { type ReportsDrill } from './reportsDrill'
import type { SalesForecast as SalesForecastWire } from '../../../../api/types'
import { PeriodPicker } from './PeriodPicker'
import { ForecastHero } from './ForecastHero'
import { ForecastSuggestions } from './ForecastSuggestions'
import { type SalesPeriod, currentPeriod } from './salesPeriod'
import { CategorisedShareCard, AttributionCard, LookAheadCard, ByCategoryCard } from './ForecastCards'
import { ForecastByRep } from './ForecastByRep'

export function ForecastTab({ onSelectDeal }: { onSelectDeal?: (id: string) => void } = {}) {
  const [period, setPeriod] = useState<SalesPeriod>(() => currentPeriod('quarter'))
  // §33: in-page drill state (the R7-1..6 shape — no URL router). Rendered
  // through the SHARED ReportsDrillView; this tab adds no drill machinery.
  const [drill, setDrill] = useState<ReportsDrill | null>(null)
  const { data, loading, error } = useAsync(
    () => fetchSalesForecast({ periodKind: period.kind, periodStart: period.start }),
    [period.kind, period.start],
  )
  // F3: the silent refetch after a write. `fresh` replaces the first read
  // for the SAME period only; a period change starts from its own read.
  const [fresh, setFresh] = useState<{ key: string; data: SalesForecastWire } | null>(null)
  const key = `${period.kind}:${period.start}`
  useEffect(() => { setFresh(null) }, [key])
  const refetch = () => {
    fetchSalesForecast({ periodKind: period.kind, periodStart: period.start })
      .then(d => setFresh({ key, data: d }))
      .catch(() => { /* the last response stays; the write's own toast has spoken */ })
  }
  const shown = fresh && fresh.key === key ? fresh.data : data
  // F6: "no deals at all" is the shell's stats read, believed only when it
  // has landed AND this response agrees (tenantHasNoDeals).
  const { stats } = useSalesFigures()
  const noDeals = !loading && !error && !!shown && tenantHasNoDeals(shown, stats)
  if (drill) {
    return <ReportsDrillView drill={drill} onBack={() => setDrill(null)} onSelectDeal={onSelectDeal} backLabel="Back to forecast" />
  }
  return (
    <div className="flex flex-col gap-4">
      <PeriodPicker period={period} onChange={setPeriod} />
      {loading && <ForecastSkeleton />}
      {!loading && error && <ErrorState message={error} />}
      {noDeals && (
        <div data-forecast-empty>
          <EmptyState message="No forecast yet" hint="Add deals to the pipeline and this page will show where the period lands." />
        </div>
      )}
      {!loading && !error && !noDeals && <ForecastView data={shown!} period={period} onDrill={setDrill} onSelectDeal={onSelectDeal} onChanged={refetch} />}
    </div>
  )
}

/** The tab's body from one wire response — pure, so the FG and FC shapes can
 *  be rendered from fixtures. `onDrill` receives the won cell's drill. */
export function ForecastView({ data: f, period, onDrill, onSelectDeal, onChanged }: { data: SalesForecastWire; period: SalesPeriod; onDrill?: (drill: ReportsDrill) => void; onSelectDeal?: (id: string) => void; onChanged?: () => void }) {
  // F4a: the grid keeps the suggestions column once this period's load had
  // suggestions, so accepting the last one (the refetch then lists none)
  // does not unmount the card and its "✓ Set" rows. A period change remounts
  // the view (the tab's loading state), which resets it.
  const pending = f.suggestions?.length ?? 0
  const [sawSuggestions, setSawSuggestions] = useState(pending > 0)
  useEffect(() => { if (pending > 0) setSawSuggestions(true) }, [pending])
  const hasSuggestions = sawSuggestions || pending > 0

  return (
    <>
      {/* F2 (register row 70): the smart forecast hero, spec §3.2 — from the
          F1 keys alone; renders nothing on a response that predates F1. */}
      <ForecastHero data={f} onSelectDeal={onSelectDeal} />

      {/* F3 + F4a (register rows 71, 72) — spec §3's grid: Suggested
          categories (2/3) beside Categorised share + Attribution (1/3). When
          nothing is pending the suggestions card renders nothing, and the
          right column's two cards share the row (spec §3.9's "let the
          confidence card span the row"). Attribution is ALWAYS present (R1,
          §18.5). */}
      <div className={hasSuggestions ? 'grid gap-4 items-start' : 'grid grid-cols-2 gap-4 items-start'}
        style={hasSuggestions ? { gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' } : undefined} data-row="suggestions">
        {hasSuggestions && <ForecastSuggestions data={f} onChanged={onChanged} onSelectDeal={onSelectDeal} />}
        {hasSuggestions
          ? <div className="flex flex-col gap-4 min-w-0"><CategorisedShareCard data={f} /><AttributionCard data={f} onChanged={onChanged} /></div>
          : <><CategorisedShareCard data={f} /><AttributionCard data={f} onChanged={onChanged} /></>}
      </div>

      {/* F4a — the look-ahead (2/3, the wire's NEXT period) beside By
          forecast category (1/3, the roll-up re-skinned). A response without
          the F1 keys renders By category full width. */}
      <div className="grid gap-4 items-start" style={f.nextPeriod ? { gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' } : undefined} data-row="look-ahead">
        {f.nextPeriod && <LookAheadCard data={f} />}
        <ByCategoryCard data={f} />
      </div>

      {/* F5 (register row 73) — By rep, spec §3.7: the rep grid with the
          target write surface (the quota grain, §18.7) and the Unattributed
          row. ForecastByRep.tsx over forecastByRep.ts. */}
      <ForecastByRep data={f} period={period} onDrill={onDrill} onChanged={onChanged} />

    </>
  )
}
